import React, { useMemo, useState } from "react";
import { format } from "d3-format";
import { useCurrentVizType } from "../../../routing/typedRoutes";
import {
  TooltipBodyRow,
  TooltipBodyRowLabel,
  TooltipBodyRowValue,
  SectorLabelWithColorIcon,
  ShowMoreLessButton,
  TooltipExploreMoreContainer,
  TooltipExploreMoreButton,
} from "@atlas-int/viz/shared";
import { ProductClass, ProductLevel, TradeDirection, TradeFlow } from "@atlas-int/data/types";
import { formatAsPercentage, formatTotalValue, computeMonetaryValueByTradeFlow, worldGroupDatum, allProductsDatum, getLocationQualifiers, ColorBy, UIView, VizType } from "@atlas-int/data";
import { getColorMap } from "@atlas-int/viz";
import { complexityColorScale } from "@atlas-int/viz/treemap";
import { googleAnalyticsCustomEvent } from "../../../sharedUtilities/analytics";
import { vizTypeIcons } from "../../../assets/icons";
import { Tooltip } from "@atlas-int/viz/shared";

import type {
  TooltipProductDatum,
  LocationLookup,
  SetQueryFn,
  L10n,
  ProductMetadataByLevel,
} from "@atlas-int/viz/shared";

interface TreemapTooltipContentProps {
  datum: TooltipProductDatum;
  totalValue: number;
  locationLookup: LocationLookup;
  colorBySelection: ColorBy;
  tradeFlow: TradeFlow;
  tradeDirection: TradeDirection;
  exporter: string;
  importer: string;
  setQuery: SetQueryFn;
  l10n: L10n;
  view: UIView;
  productMetadata: ProductMetadataByLevel | undefined;
  productClass: ProductClass;
  product: string;
}

/**
 * Tooltip content for TreeMap visualization
 */
export const TreemapTooltipContent: React.FC<TreemapTooltipContentProps> = ({
  datum,
  totalValue,
  locationLookup,
  colorBySelection,
  tradeFlow,
  tradeDirection,
  exporter,
  importer,
  setQuery,
  l10n,
  view,
  productMetadata,
  productClass,
  product,
}) => {
  const [showMore, setShowMore] = useState<boolean>(false);
  const toggleShowMore = () => setShowMore(!showMore);
  const valueFormatterToUse = (value: number) => `$${formatTotalValue(value)}`;

  const {
    topLevelParent,
    productLevel,
    exportRca,
    distance,
    pci,
    productId,
  } = datum;

  const { importerIsWorld } = getLocationQualifiers({
    exporter,
    importer,
  });

  let colorMap = getColorMap({
    view: UIView.Products,
    productClass: productClass,
  });
  let fillColor = colorMap ? colorMap.get(topLevelParent) : undefined;

  const matchingSector = productMetadata?.section.find(
    (productSection) => productSection.productId === topLevelParent,
  );

  const valueToUse = computeMonetaryValueByTradeFlow({
    datum,
    tradeFlow: tradeFlow,
    tradeDirection,
  });

  const sectorFillColor =
    colorBySelection === ColorBy.Sector ? fillColor : undefined;
  const pciFillColor =
    colorBySelection === ColorBy.Complexity
      ? complexityColorScale(pci as number)
      : undefined;

  const pciContentRow = (
    <TooltipBodyRow>
      <TooltipBodyRowLabel>
        <Tooltip
          explanation={l10n.getString("settings-pci-tooltipText")}
          title={l10n.getString("settings-pci-title")}
          inlineTooltip={true}
        >
          {l10n.getString("settings-pci-title")}
        </Tooltip>
      </TooltipBodyRowLabel>
      <TooltipBodyRowValue>
        <SectorLabelWithColorIcon $fillColor={pciFillColor}>
          {format(",.3f")(pci as number)}
        </SectorLabelWithColorIcon>
      </TooltipBodyRowValue>
    </TooltipBodyRow>
  );

  const ProductViewTooltipButtons = useMemo(
    () =>
      ({
        locationName: _locationName,
        productId,
        productName: _productName,
        exporter,
        importer,
        locationLookup,
        setQuery,
      }: {
        locationName: string;
        productId: string;
        productName?: string;
        exporter: string;
        importer: string;
        locationLookup: LocationLookup;
        setQuery: SetQueryFn;
      }) => {
        const isWorldExporter =
          exporter === worldGroupDatum.groupId &&
          importer === worldGroupDatum.groupId;
        const isWorldImporter =
          importer === worldGroupDatum.groupId &&
          exporter === worldGroupDatum.groupId;
        const isWorldBoth = isWorldExporter || isWorldImporter;
        const exporterName =
          locationLookup.get(exporter)?.nameEn ||
          locationLookup.get(exporter)?.groupName;
        const importerName =
          locationLookup.get(importer)?.nameEn ||
          locationLookup.get(importer)?.groupName;
        // Case 1: World is either importer or exporter (or both)
        if (isWorldBoth) {
          return (
            <TooltipExploreMoreContainer>
              <h2>For this product explore:</h2>
              <TooltipExploreMoreButton
                icon={vizTypeIcons.treemap}
                onClick={() => {
                  googleAnalyticsCustomEvent(
                    "tooltipbutton",
                    "click",
                    "1",
                    "explore",
                  );
                  setQuery({
                    view: "markets",
                    exporter: worldGroupDatum.groupId,
                    importer: worldGroupDatum.groupId,
                    tradeDirection: "exports",
                    product: productId,
                    colorBy: undefined,
                  });
                }}
              >
                Who exported this product?
              </TooltipExploreMoreButton>
              <TooltipExploreMoreButton
                icon={vizTypeIcons.treemap}
                onClick={() => {
                  googleAnalyticsCustomEvent(
                    "tooltipbutton",
                    "click",
                    "2",
                    "explore",
                  );
                  setQuery({
                    view: "markets",
                    exporter: worldGroupDatum.groupId,
                    importer: worldGroupDatum.groupId,
                    tradeDirection: "imports",
                    product: productId,
                    colorBy: undefined,
                  });
                }}
              >
                Who imported this product?
              </TooltipExploreMoreButton>
            </TooltipExploreMoreContainer>
          );
        }

        // Case 2: Specific exporter, World importer
        if (!isWorldBoth && importer === worldGroupDatum.groupId) {
          return (
            <TooltipExploreMoreContainer>
              <h2>For this product explore:</h2>
              <TooltipExploreMoreButton
                icon={vizTypeIcons.treemap}
                onClick={() => {
                  googleAnalyticsCustomEvent(
                    "tooltipbutton",
                    "click",
                    "1",
                    "explore",
                  );
                  setQuery({
                    view: "markets",
                    exporter: worldGroupDatum.groupId,
                    importer: worldGroupDatum.groupId,
                    tradeDirection: "exports",
                    product: productId,
                    colorBy: undefined,
                  });
                }}
              >
                Who exported this product?
              </TooltipExploreMoreButton>
              <TooltipExploreMoreButton
                icon={vizTypeIcons.treemap}
                onClick={() => {
                  googleAnalyticsCustomEvent(
                    "tooltipbutton",
                    "click",
                    "2",
                    "explore",
                  );
                  setQuery({
                    view: "markets",
                    exporter: exporter,
                    importer: worldGroupDatum.groupId,
                    product: productId,
                    colorBy: undefined,
                  });
                }}
              >
                {`Where did ${exporterName} export this product to?`}
              </TooltipExploreMoreButton>
            </TooltipExploreMoreContainer>
          );
        }

        // Case 3: World exporter, Specific importer
        if (exporter === worldGroupDatum.groupId && !importerIsWorld) {
          return (
            <TooltipExploreMoreContainer>
              <h2>For this product explore:</h2>
              <TooltipExploreMoreButton
                icon={vizTypeIcons.treemap}
                onClick={() => {
                  googleAnalyticsCustomEvent(
                    "tooltipbutton",
                    "click",
                    "1",
                    "explore",
                  );
                  setQuery({
                    view: "markets",
                    exporter: worldGroupDatum.groupId,
                    importer: worldGroupDatum.groupId,
                    tradeDirection: "imports",
                    product: productId,
                    colorBy: undefined,
                  });
                }}
              >
                Who imported this product?
              </TooltipExploreMoreButton>
              <TooltipExploreMoreButton
                icon={vizTypeIcons.treemap}
                onClick={() => {
                  googleAnalyticsCustomEvent(
                    "tooltipbutton",
                    "click",
                    "2",
                    "explore",
                  );
                  setQuery({
                    view: "markets",
                    exporter: worldGroupDatum.groupId,
                    importer: importer,
                    product: productId,
                    colorBy: undefined,
                  });
                }}
              >
                {`Where did ${importerName} import this product from?`}
              </TooltipExploreMoreButton>
            </TooltipExploreMoreContainer>
          );
        }

        // Case 4: Specific exporter and importer
        return (
          <TooltipExploreMoreContainer>
            <h2>For this product explore:</h2>
            <TooltipExploreMoreButton
              icon={vizTypeIcons.treemap}
              onClick={() => {
                googleAnalyticsCustomEvent(
                  "tooltipbutton",
                  "click",
                  "1",
                  "explore",
                );
                setQuery({
                  view: "markets",
                  exporter: exporter,
                  importer: worldGroupDatum.groupId,
                  product: productId,
                  colorBy: undefined,
                });
              }}
            >
              {`Where did ${exporterName} export this product to?`}
            </TooltipExploreMoreButton>
            <TooltipExploreMoreButton
              icon={vizTypeIcons.treemap}
              onClick={() => {
                googleAnalyticsCustomEvent(
                  "tooltipbutton",
                  "click",
                  "2",
                  "explore",
                );
                setQuery({
                  view: "markets",
                  exporter: worldGroupDatum.groupId,
                  importer: importer,
                  product: productId,
                  colorBy: undefined,
                });
              }}
            >
              {`Where did ${importerName} import this product from?`}
            </TooltipExploreMoreButton>
          </TooltipExploreMoreContainer>
        );
      },
    [importerIsWorld],
  );

  const LocationViewTooltipButtons = ({
    locationName,
    productId,
    productName,
    exporter,
    importer,
    locationLookup,
    setQuery,
    lid,
    valueFormatterToUse: _valueFormatterToUse,
  }: {
    locationName: string;
    productId: string;
    productName?: string;
    exporter: string;
    importer: string;
    locationLookup: LocationLookup;
    setQuery: SetQueryFn;
    lid: string;
    valueFormatterToUse: (value: number) => string;
  }) => {
    const isWorldExporter = exporter === worldGroupDatum.groupId;
    const isWorldImporter = importer === worldGroupDatum.groupId;
    const isWorldBoth = isWorldExporter && isWorldImporter;
    const isAllProducts = productId === allProductsDatum.productId;

    // Case 1: World + All Products
    if (isWorldBoth && isAllProducts) {
      const isExporting = tradeDirection === TradeDirection.Exports;
      return (
        <TooltipExploreMoreContainer>
          <h2>For this location explore:</h2>
          <TooltipExploreMoreButton
            icon={vizTypeIcons.treemap}
            onClick={() => {
              googleAnalyticsCustomEvent(
                "tooltipbutton",
                "click",
                "1",
                "explore",
              );
              setQuery({
                view: "products",
                exporter: isExporting ? lid : worldGroupDatum.groupId,
                importer: isExporting ? worldGroupDatum.groupId : lid,
                tradeDirection: isExporting
                  ? TradeDirection.Exports
                  : TradeDirection.Imports,
                product: allProductsDatum.productId,
                colorBy: undefined,
              });
            }}
          >
            {`What products did ${locationName} ${isExporting ? "export" : "import"}?`}
          </TooltipExploreMoreButton>
          <TooltipExploreMoreButton
            icon={vizTypeIcons.treemap}
            onClick={() => {
              googleAnalyticsCustomEvent(
                "tooltipbutton",
                "click",
                "2",
                "explore",
              );
              setQuery({
                view: "markets",
                exporter: isExporting ? String(datum.id) : worldGroupDatum.groupId,
                importer: isExporting ? worldGroupDatum.groupId : String(datum.id),
                tradeDirection: isExporting
                  ? TradeDirection.Exports
                  : TradeDirection.Imports,
                product: allProductsDatum.productId,
                colorBy: undefined,
              });
            }}
          >
            {`Where did ${locationName} ${isExporting ? "export to" : "import from"}?`}
          </TooltipExploreMoreButton>
        </TooltipExploreMoreContainer>
      );
    }
    // Case 2: World + Specific Product
    if (isWorldBoth && !isAllProducts) {
      const isExporting = tradeDirection === TradeDirection.Exports;
      return (
        <TooltipExploreMoreContainer>
          <h2>For this location explore:</h2>
          <TooltipExploreMoreButton
            icon={vizTypeIcons.treemap}
            onClick={() => {
              googleAnalyticsCustomEvent(
                "tooltipbutton",
                "click",
                "1",
                "explore",
              );
              setQuery({
                view: "markets",
                exporter: isExporting ? String(datum.id) : worldGroupDatum.groupId,
                importer: isExporting ? worldGroupDatum.groupId : String(datum.id),
                tradeDirection: isExporting
                  ? TradeDirection.Exports
                  : TradeDirection.Imports,
                product: productId,
                colorBy: undefined,
              });
            }}
          >
            {`Where did ${locationName} ${isExporting ? "export" : "import"} ${productName} ${isExporting ? "to" : "from"}?`}
          </TooltipExploreMoreButton>
          <TooltipExploreMoreButton
            icon={vizTypeIcons.treemap}
            onClick={() => {
              googleAnalyticsCustomEvent(
                "tooltipbutton",
                "click",
                "2",
                "explore",
              );
              setQuery({
                view: "markets",
                exporter: isExporting ? String(datum.id) : worldGroupDatum.groupId,
                importer: isExporting ? worldGroupDatum.groupId : String(datum.id),
                tradeDirection: isExporting
                  ? TradeDirection.Exports
                  : TradeDirection.Imports,
                product: allProductsDatum.productId,
                colorBy: undefined,
              });
            }}
          >
            {`Where  did ${locationName} ${isExporting ? "export to" : "import from"}?`}
          </TooltipExploreMoreButton>
        </TooltipExploreMoreContainer>
      );
    }

    // Case 3: Specific Countries + All Products
    if (!isWorldBoth && isAllProducts) {
      const isExporting = importer === worldGroupDatum.groupId; // If World is importer, we're looking at exports
      const selectedCountryName = isExporting
        ? locationLookup.get(exporter)?.nameEn ||
          locationLookup.get(exporter)?.groupName
        : locationLookup.get(importer)?.nameEn ||
          locationLookup.get(importer)?.groupName ||
          "The World";
      const selectedCountryId = isExporting ? exporter : importer;
      const partnerCountryId = datum.id as string;
      const partnerCountryData = locationLookup.get(partnerCountryId);
      const partnerCountryName =
        partnerCountryData?.nameEn ||
        partnerCountryData?.groupName ||
        "Unknown";
      return (
        <TooltipExploreMoreContainer>
          <h2>For this location explore:</h2>
          <TooltipExploreMoreButton
            icon={vizTypeIcons.treemap}
            onClick={() => {
              googleAnalyticsCustomEvent(
                "tooltipbutton",
                "click",
                "1",
                "explore",
              );
              setQuery({
                view: "products",
                exporter: isExporting ? selectedCountryId : String(datum.id),
                importer: isExporting ? String(datum.id) : selectedCountryId,
                tradeDirection: isExporting
                  ? TradeDirection.Exports
                  : TradeDirection.Imports,
                product: allProductsDatum.productId,
                colorBy: undefined,
              });
            }}
          >
            {`What products did ${selectedCountryName} ${isExporting ? "export" : "import"} ${isExporting ? "to" : "from"} ${partnerCountryName}?`}
          </TooltipExploreMoreButton>
          <TooltipExploreMoreButton
            icon={vizTypeIcons.treemap}
            onClick={() => {
              googleAnalyticsCustomEvent(
                "tooltipbutton",
                "click",
                "2",
                "explore",
              );
              setQuery({
                view: "markets",
                exporter: isExporting
                  ? partnerCountryId
                  : worldGroupDatum.groupId,
                importer: isExporting
                  ? worldGroupDatum.groupId
                  : partnerCountryId,
                tradeDirection: isExporting
                  ? TradeDirection.Exports
                  : TradeDirection.Imports,
                product: allProductsDatum.productId,
                colorBy: undefined,
              });
            }}
          >
            {`Where did ${partnerCountryName} ${isExporting ? "export to" : "import from"}?`}
          </TooltipExploreMoreButton>
        </TooltipExploreMoreContainer>
      );
    }

    // Case 4: Specific Countries + Specific Product
    const isExporting = importer === worldGroupDatum.groupId;

    return (
      <TooltipExploreMoreContainer>
        <h2>For this location explore:</h2>
        <TooltipExploreMoreButton
          icon={vizTypeIcons.treemap}
          onClick={() => {
            googleAnalyticsCustomEvent(
              "tooltipbutton",
              "click",
              "1",
              "explore",
            );
            setQuery({
              view: "markets",
              exporter: isExporting ? String(datum.id) : worldGroupDatum.groupId,
              importer: isExporting ? worldGroupDatum.groupId : String(datum.id),
              tradeDirection: isExporting
                ? TradeDirection.Exports
                : TradeDirection.Imports,
              product: productId,
              colorBy: undefined,
            });
          }}
        >
          {`Where did ${locationName} ${isExporting ? "export" : "import"} ${productName} ${isExporting ? "to" : "from"}?`}
        </TooltipExploreMoreButton>
        <TooltipExploreMoreButton
          icon={vizTypeIcons.treemap}
          onClick={() => {
            googleAnalyticsCustomEvent(
              "tooltipbutton",
              "click",
              "2",
              "explore",
            );
            setQuery({
              view: "markets", // Changed from "products" to "markets" to show destinations/sources
              exporter: isExporting ? lid : worldGroupDatum.groupId,
              importer: isExporting ? worldGroupDatum.groupId : lid,
              tradeDirection: isExporting
                ? TradeDirection.Exports
                : TradeDirection.Imports,
              product: allProductsDatum.productId,
              colorBy: undefined,
            });
          }}
        >
          {`Where did ${locationName} ${isExporting ? "export to" : "import from"}?`}
        </TooltipExploreMoreButton>
      </TooltipExploreMoreContainer>
    );
  };

  // Get the product name from metadata
  const selectedProductMetadata =
    productMetadata?.fourDigit?.find(
      (p) => p.productId === product,
    ) ||
    productMetadata?.section?.find((p) => p.productId === product);
  const vizType = useCurrentVizType();

  const tooltipButtons = useMemo(() => {
    if (vizType !== VizType.Tree) {
      return null;
    }
    const locationId = datum.partnerCountryId as string;
    const hoveredLocationName =
      (datum.nameEn || datum.groupName || "The World") as string;

    const productName =
      (selectedProductMetadata?.nameShortEn || selectedProductMetadata?.nameEn) as string | undefined;

    // Check if we're in Markets view
    if (view === UIView.Markets) {
      return (
        <LocationViewTooltipButtons
          locationName={hoveredLocationName}
          productId={product}
          productName={productName}
          exporter={exporter}
          importer={importer}
          locationLookup={locationLookup}
          setQuery={setQuery}
          lid={locationId}
          valueFormatterToUse={valueFormatterToUse}
        />
      );
    }

    // If not Markets view, render ProductViewTooltipButtons
    return (
      <ProductViewTooltipButtons
        locationName={hoveredLocationName}
        productId={productId as string}
        productName={productName}
        exporter={exporter}
        importer={importer}
        locationLookup={locationLookup}
        setQuery={setQuery}
      />
    );
  }, [
    vizType,
    datum,
    product,
    selectedProductMetadata,
    view,
    locationLookup,
    exporter,
    importer,
    setQuery,
  ]);

  if (!showMore) {
    return (
      <>
        {matchingSector && (
          <TooltipBodyRow>
            <TooltipBodyRowLabel>Sector:</TooltipBodyRowLabel>
            <TooltipBodyRowValue>
              <SectorLabelWithColorIcon $fillColor={sectorFillColor}>
                {matchingSector && matchingSector.nameShortEn}
              </SectorLabelWithColorIcon>
            </TooltipBodyRowValue>
          </TooltipBodyRow>
        )}
        <TooltipBodyRow>
          <TooltipBodyRowLabel>
            {tradeDirection === TradeDirection.Exports
              ? "Export Value"
              : "Import Value"}
            :
          </TooltipBodyRowLabel>
          <TooltipBodyRowValue>
            {valueFormatterToUse && valueFormatterToUse(valueToUse)}
          </TooltipBodyRowValue>
        </TooltipBodyRow>
        <TooltipBodyRow>
          <TooltipBodyRowLabel>Share:</TooltipBodyRowLabel>
          <TooltipBodyRowValue>
            {totalValue && formatAsPercentage(valueToUse / totalValue)}%
          </TooltipBodyRowValue>
        </TooltipBodyRow>
        {colorBySelection === ColorBy.Complexity ? pciContentRow : null}
        {productLevel === ProductLevel.Product4digit && (
          <TooltipBodyRow>
            <TooltipBodyRowLabel>
              <ShowMoreLessButton onClick={toggleShowMore}>
                Show more »
              </ShowMoreLessButton>
            </TooltipBodyRowLabel>
          </TooltipBodyRow>
        )}
        {tooltipButtons}
      </>
    );
  } else {
    return (
      <>
        <TooltipBodyRow>
          <TooltipBodyRowLabel>
            <Tooltip
              explanation={l10n.getString("settings-rca-tooltipText")}
              title={l10n.getString("settings-rca-title")}
              inlineTooltip={true}
            >
              {l10n.getString("settings-rca-title")}
            </Tooltip>
          </TooltipBodyRowLabel>
          <TooltipBodyRowValue>{format(",.2f")(exportRca as number)}</TooltipBodyRowValue>
        </TooltipBodyRow>
        <TooltipBodyRow>
          <TooltipBodyRowLabel>Distance:</TooltipBodyRowLabel>
          <TooltipBodyRowValue>{format(",.3f")(distance as number)}</TooltipBodyRowValue>
        </TooltipBodyRow>
        {colorBySelection === ColorBy.Sector ? pciContentRow : null}
        <TooltipBodyRow>
          <TooltipBodyRowLabel>
            <ShowMoreLessButton onClick={toggleShowMore}>
              Show less «
            </ShowMoreLessButton>
          </TooltipBodyRowLabel>
        </TooltipBodyRow>
      </>
    );
  }
};
